import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { authAxios, storeTokens, getAccessToken, initializeAuth, clearTokens, reportDeviceFingerprint, getMe, getTcStatus, invalidateMeCache } from "./utils/auth";
import { computeDeviceFingerprint } from "./utils/deviceFingerprint";
import { CoinGateProvider } from "./contexts/CoinGateContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import BetaTerms from "./pages/BetaTerms";
import Dashboard from "./pages/Dashboard";
import ProfileSetup from "./pages/ProfileSetup";
import SocialConnections from "./pages/SocialConnections";
import MatchesGallery from "./pages/MatchesGallery";
import MatchDetail from "./pages/MatchDetail";
import ErosChat from "./pages/ErosChat";
import ErosCoach from "./pages/ErosCoach";
import ErosAdvice from "./pages/ErosAdvice";
import Settings from "./pages/Settings";
import AdminPanel from "./pages/AdminPanel";
import UserPage from "./pages/UserPage";
import LearningResourceDetail from "./pages/LearningResourceDetail";
import LearningProfileDetail from "./pages/LearningProfileDetail";
import RelationshipEditor from "./pages/RelationshipEditor";
import SectionEditor from "./pages/SectionEditor";
import DataSourceView from "./pages/DataSourceView";
import CompatibilityReport from "./pages/CompatibilityReport";
import TestChat from "./pages/TestChat";
import TestCoinReveal from "./pages/TestCoinReveal";
import PromptLog from "./pages/PromptLog";
import PromptLogDetail from "./pages/PromptLogDetail";
import PromptLogChatReview from "./pages/PromptLogChatReview";
import AuditSiteWide from "./pages/audit/AuditSiteWide";
import AuditPerUser from "./pages/audit/AuditPerUser";
import AuditBonusPool from "./pages/audit/AuditBonusPool";
import PromptLogTokens from "./pages/PromptLogTokens";
import PromptLogCredits from "./pages/PromptLogCredits";
import PromptLogAffLedger from "./pages/PromptLogAffLedger";
import PromptLogCommissionLedgerV2 from "./pages/PromptLogCommissionLedgerV2";
import PayoutBatchesPage from "./pages/PayoutBatchesPage";
import PromptLogBonusPool from "./pages/PromptLogBonusPool";
import CoachLint from "./pages/CoachLint";
import ContactPage from "./pages/ContactPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import Helpdesk from "./pages/Helpdesk";
import News from "./pages/News";
import ResetPassword from "./pages/ResetPassword";
import AffiliatePage from "./pages/AffiliatePage";
import SubscriptionPlansPage from "./pages/SubscriptionPlansPage";
import OpsRunbookPage from "./pages/OpsRunbookPage";
import OnboardingWizard from "./pages/OnboardingWizard";
import { resolveLoginDestination } from "./utils/onboardingTrigger";
import PrivacyStatement from "./pages/PrivacyStatement";
import TermsPage from "./pages/TermsPage";
import WaitlistPage from "./pages/WaitlistPage";
import StatsPage from "./pages/StatsPage";
import MailerPage from "./pages/MailerPage";
import VerifiedPage from "./pages/VerifiedPage";
import EmailPreferences from "./pages/EmailPreferences";
import AnalyticsCommandCenter from "./pages/AnalyticsCommandCenter";
import SalesAnalytics from "./pages/SalesAnalytics";
import ForumPage from "./pages/ForumPage";
import BlockedPage from "./pages/BlockedPage";
import ClusterDetail from "./pages/admin/ClusterDetail";
import FraudDefender from "./pages/admin/FraudDefender";
import StripeConnectInfoPage from "./pages/StripeConnectInfoPage";
import NoticeBlock from "./components/NoticeBlock";
import { lazy, Suspense } from "react";
const ReadPage = lazy(() => import("./pages/ReadPage"));
import { useAnalytics } from "./hooks/useAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// /join/:affId redirects to /auth with pre-populated referral code
function JoinRedirect() {
  const { affId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (affId) {
      localStorage.setItem("ref_affiliate", affId);
    }
    navigate(`/auth?ref=${encodeURIComponent(affId || "")}`, { replace: true });
  }, [affId, navigate]);
  return null;
}

// Legacy forum-post deep links from older notifications used the path
// `/forum/post/:postId`. The canonical route is `/forum?post=:postId` (which
// ForumPage reads via useSearchParams). This shim rewrites the URL so old
// notifications and emails still resolve correctly.
function LegacyForumPostRedirect() {
  const { postId } = useParams();
  if (!postId) return <Navigate to="/forum" replace />;
  return <Navigate to={`/forum?post=${encodeURIComponent(postId)}`} replace />;
}

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Auth Callback Component - handles Google OAuth return
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.get(`${API}/auth/session`, {
            headers: { "X-Session-ID": sessionId },
            withCredentials: true
          });
          
          // Store tokens if returned (new auth system)
          if (response.data.access_token && response.data.refresh_token) {
            // Strip tokens from user data before storing
            const { access_token, refresh_token, ...userData } = response.data;
            storeTokens(access_token, refresh_token, userData);
          }
          
          // Clear hash and navigate to dashboard with user data
          window.history.replaceState(null, "", window.location.pathname);
          const { access_token: _a, refresh_token: _r, ...user } = response.data;
          // Fix A: resolve onboarding destination BEFORE navigate so Dashboard
          // never mounts for users who still need the wizard (kills the
          // news-popup phantom view-count race).
          const dest = await resolveLoginDestination("/dashboard");
          navigate(dest, { state: { user }, replace: true });
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/auth", { replace: true });
        }
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-divine-black flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-divine-gold border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-divine-gold font-cinzel">Eros is blessing your arrival...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { changeTheme, syncUserTheme, theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tcNeedsReprompt, setTcNeedsReprompt] = useState(null); // null=unchecked, true, false
  const hasLoadedTheme = useRef(false);
  const hasCheckedAuth = useRef(false);

  const onBetaTermsPage = location.pathname === "/beta-terms";

  useEffect(() => {
    // Sync theme if user came from login with state (only once)
    if (location.state?.user && location.state.user.theme && !hasLoadedTheme.current) {
      syncUserTheme(location.state.user.theme);
      hasLoadedTheme.current = true;
    }

    // Re-verify auth + T&C status on every route change.
    // ProtectedRoute is reused across routes (e.g. /beta-terms → /dashboard),
    // so stale state from a prior route (like tcNeedsReprompt=true) would
    // otherwise block the new page from rendering (black screen).
    // Reset the guards, show spinner, then re-check.
    hasCheckedAuth.current = false;
    setTcNeedsReprompt(null);

    if (hasCheckedAuth.current) return;
    hasCheckedAuth.current = true;

    const checkAuth = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
        setIsAuthenticated(true);

        // Fraud Guardrails Phase 2 (A+C) — enroll device fingerprint on
        // app boot for users who signed up before Phase 2. Fire-and-forget.
        (async () => {
          try {
            const fp = await computeDeviceFingerprint();
            if (fp) reportDeviceFingerprint(fp);
          } catch { /* non-fatal */ }
        })();

        if (userData.theme && !hasLoadedTheme.current) {
          syncUserTheme(userData.theme);
          hasLoadedTheme.current = true;
        }

        // Beta T&C gate — check status ONCE per protected mount.
        // If a reprompt is required and we're not already on the T&C page,
        // redirect to /beta-terms. This runs after every login/refresh.
        try {
          const tc = await getTcStatus();
          setTcNeedsReprompt(!!tc?.needs_reprompt);
          if (tc?.needs_reprompt && !onBetaTermsPage) {
            navigate("/beta-terms", { replace: true });
          }
        } catch (tcErr) {
          // Non-fatal — if status check fails, don't block the user
          console.warn("tc-status check failed:", tcErr);
          setTcNeedsReprompt(false);
        }
      } catch (error) {
        // Only log the user out on a genuine auth failure (401 after the
        // refresh interceptor already tried and failed). Any other status —
        // 429 rate limit, 5xx during a deploy, network flap — must NOT nuke
        // their session; we just leave state as-is and let the next route
        // change (or user retry) succeed. Prior to this guard, a single
        // 429 during a navigation burst was clearing tokens and bouncing
        // users to /auth every ~15 min.
        const status = error?.response?.status;
        if (status === 401) {
          setIsAuthenticated(false);
          clearTokens();
          navigate("/auth", { replace: true });
        } else {
          console.warn("checkAuth non-fatal error, keeping session:", status, error?.message);
          // Keep isAuthenticated as whatever it was; if this was the first
          // check (still null), assume authenticated so the app renders
          // and the user can retry — refresh interceptor will handle any
          // subsequent 401.
          setIsAuthenticated((prev) => prev === null ? true : prev);
          setTcNeedsReprompt((prev) => prev === null ? false : prev);
        }
      }
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (isAuthenticated === null || tcNeedsReprompt === null) {
    return (
      <div className="min-h-screen bg-divine-black flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) return null;
  // Hard-block: if T&C reprompt required and we're not on the T&C page,
  // render nothing (the useEffect above has already dispatched a navigate).
  if (tcNeedsReprompt && !onBetaTermsPage) return null;
  // Defender: if the current user got blocked while their tab was open,
  // route them out of every protected page to /blocked. NoticeBlock
  // handles the warning/frozen banner separately.
  if (user?.account_status === "blocked" || user?.blocked) {
    if (location.pathname !== "/blocked") {
      navigate("/blocked", { replace: true });
      return null;
    }
  }

  // Function to refresh user data from server (bypasses the 30s cache)
  const refreshUser = async () => {
    try {
      const data = await getMe({ force: true });
      setUser(data);
      return data;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser }}>
      <NoticeBlock />
      {children}
    </AuthContext.Provider>
  );
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  useAnalytics();

  // Detect session_id during render (NOT in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route
        path="/beta-terms"
        element={
          <ProtectedRoute>
            <BetaTerms />
          </ProtectedRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<PrivacyStatement />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/verified" element={<VerifiedPage />} />
      <Route path="/email-preferences/:sub_id" element={<EmailPreferences />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/blog/:slug" element={<Suspense fallback={<div className="min-h-screen bg-[#0a0a0a]" />}><ReadPage /></Suspense>} />
      <Route
        path="/waitlist"
        element={
          <ProtectedRoute>
            <WaitlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <StatsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-resource/:resourceType"
        element={
          <ProtectedRoute>
            <LearningResourceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-profile/:profileId"
        element={
          <ProtectedRoute>
            <LearningProfileDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-source/:sourceId"
        element={
          <ProtectedRoute>
            <DataSourceView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            <SocialConnections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships"
        element={
          <ProtectedRoute>
            <MatchesGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships/new"
        element={
          <ProtectedRoute>
            <RelationshipEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships/:matchId"
        element={
          <ProtectedRoute>
            <MatchDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships/:matchId/edit"
        element={
          <ProtectedRoute>
            <RelationshipEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships/:matchId/edit-section"
        element={
          <ProtectedRoute>
            <SectionEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relationships/:matchId/compatibility"
        element={
          <ProtectedRoute>
            <CompatibilityReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ErosChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/session/:sessionId"
        element={
          <ProtectedRoute>
            <ErosChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:matchId"
        element={
          <ProtectedRoute>
            <ErosChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/:matchId/:sessionId"
        element={
          <ProtectedRoute>
            <ErosChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social-ai"
        element={
          <ProtectedRoute>
            <ErosAdvice />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Navigate to="/profile?tab=settings" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute>
            <UserPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fraud/cluster/:kind/:key"
        element={
          <ProtectedRoute>
            <ClusterDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/fraud/defender/:userId"
        element={
          <ProtectedRoute>
            <FraudDefender />
          </ProtectedRoute>
        }
      />
      <Route path="/blocked" element={<BlockedPage />} />
      <Route
        path="/testchat"
        element={
          <ProtectedRoute>
            <TestChat />
          </ProtectedRoute>
        }
      />
      {process.env.NODE_ENV !== "production" && (
        <Route path="/test/coin-reveal" element={<TestCoinReveal />} />
      )}
      <Route
        path="/eroscoach"
        element={
          <ProtectedRoute>
            <ErosCoach />
          </ProtectedRoute>
        }
      />
      <Route
        path="/eroscoach/:matchId"
        element={
          <ProtectedRoute>
            <ErosCoach />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog"
        element={
          <ProtectedRoute>
            <PromptLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/:userId"
        element={
          <ProtectedRoute>
            <PromptLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/prompt/:sessionId/:logId"
        element={
          <ProtectedRoute>
            <PromptLogDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/chat/:sessionId"
        element={
          <ProtectedRoute>
            <PromptLogChatReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/audit"
        element={
          <ProtectedRoute>
            <AuditSiteWide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/audit/user/:userId"
        element={
          <ProtectedRoute>
            <AuditPerUser />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/audit/bonus-pool"
        element={
          <ProtectedRoute>
            <AuditBonusPool />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/tokens"
        element={
          <ProtectedRoute>
            <PromptLogTokens />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/tokens/:userId"
        element={
          <ProtectedRoute>
            <PromptLogTokens />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/credits"
        element={
          <ProtectedRoute>
            <PromptLogCredits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/credits/:userId"
        element={
          <ProtectedRoute>
            <PromptLogCredits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/bonus-pool"
        element={
          <ProtectedRoute>
            <PromptLogBonusPool />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/bonus-pool/:userId"
        element={
          <ProtectedRoute>
            <PromptLogBonusPool />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/aff_acc"
        element={
          <ProtectedRoute>
            <PromptLogAffLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/aff_acc/:userId"
        element={
          <ProtectedRoute>
            <PromptLogAffLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plog/commission_ledger_v2"
        element={
          <ProtectedRoute>
            <PromptLogCommissionLedgerV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payouts"
        element={
          <ProtectedRoute>
            <PayoutBatchesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach-lint"
        element={
          <ProtectedRoute>
            <CoachLint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contact"
        element={
          <ProtectedRoute>
            <ContactPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/helpdesk"
        element={
          <ProtectedRoute>
            <Helpdesk />
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            <News />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate"
        element={
          <ProtectedRoute>
            <AffiliatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/affiliate/stripe-connect-info"
        element={
          <ProtectedRoute>
            <StripeConnectInfoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <SubscriptionPlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsCommandCenter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sales"
        element={
          <ProtectedRoute>
            <SalesAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/runbook"
        element={
          <ProtectedRoute>
            <OpsRunbookPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forum/cat/:hashtag"
        element={
          <ProtectedRoute>
            <ForumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forum/post/:postId"
        element={<LegacyForumPostRedirect />}
      />
      <Route
        path="/forum"
        element={
          <ProtectedRoute>
            <ForumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mailer"
        element={
          <ProtectedRoute>
            <MailerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/join/:affId"
        element={<JoinRedirect />}
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-divine-black">
        <BrowserRouter>
          <CoinGateProvider>
            <AppRouter />
          </CoinGateProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
